import {
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  styled,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from "@mui/material"
import { Box } from "@mui/system"
import React, { useEffect, useState } from "react"
import styles from "./EditQuestion.module.scss"
import CloseIcon from "@mui/icons-material/Close"
import AppTextField from "components/StyledComponents/AppTextField"
import ImageIcon from "assets/icons/ImageIcon.svg"
import { Formik } from "formik"
import { validationSchemaEditWeeklyQuestion } from "helpers/validationSchema"
import AppButton from "components/StyledComponents/AppButton"
import DeleteImageIcon from "assets/icons/closeIcon.svg"
import { useDispatch, useSelector } from "react-redux"
import { uploadSuggestedQuestionImageAction } from "redux/L&D/actions"
import { learningDevelopmentActions } from "redux/L&D/slice/user"
import DraggableOptions from "components/DraggableOptions"
import { editChallengeQuestionAction, updateCommentStatusAction } from "redux/marketing/actions"
import ImageUploadPopup from "pages/private/learning_development/trivia-contest/components/ImageUploadPopup"
import AppSelect from "components/StyledComponents/AppSelect"
import DraggableImageOptions from "components/DraggableImageOptions"
import DraggableMatchingOptions from "components/DraggableMatchingOptions"
import { useTheme } from "@emotion/react"
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked"
import Comment from "../Comment/comment"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import { statusOptions } from "helpers/constant"

const StyledDialog = styled(Dialog)(() => ({
  "& .MuiPaper-root": {
    backgroundColor: "transparent"
  }
}))

const questionTypeChoices = [
  { label: "Text", value: "text" },
  { label: "Matching", value: "matching" },
  { label: "2-Images", value: "2-images" },
  { label: "4-Images", value: "4-images" }
]

const noCommentsTextMap = {
  open_comments: "No open comments available",
  resolved_comments: "No resolved comments available",
  irrelevant_comments: "No irrelevant comments available"
}

const EditQuestion = ({ onClose = () => {}, data = {}, showRank, RankOpt, activeTab }) => {
  const [question, setQuestion] = useState({
    optionSet: [{ name: "", isCorrect: false }]
  })
  const [questionInitOption, setQuestionInitOption] = useState([{ name: "", isCorrect: false }])
  const [originalOptionsBackup, setOriginalOptionsBackup] = useState(null)
  const [previousQuestionType, setPreviousQuestionType] = useState(null)
  const [isImagePopupOpen, setIsImagePopupOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [tab, setTab] = useState("open_comments")
  const { image } = useSelector((store) => store?.learningDevelopment?.suggestedQuestion)
  const { weeklyCategoryList } = useSelector((store) => store?.marketing)
  const { flavourImages } = useSelector((store) => store?.marketing?.wineOfTheMonth || {})

  const { commentsData } = useSelector((store) => store?.marketing)
  const [selectedComments, setSelectedComments] = useState([])
  const allCommentIds = commentsData?.[tab]?.map((c) => c.id) || []
  const [excepted_ids, setExcepted_ids] = useState([])
  const [showComments, setShowComments] = useState(false)
  const isAllSelected =
    selectedComments?.length === allCommentIds?.length && allCommentIds?.length > 0
  const theme = useTheme()

  const dispatch = useDispatch()

  const handleToggleAll = () => {
    if (isAllSelected) {
      setSelectedComments([])
    } else {
      setSelectedComments(allCommentIds)
    }
  }

  const handleToggleOne = (id) => {
    if (isAllSelected) {
      setSelectedComments((prev) => prev?.filter((c) => c !== id))
      setExcepted_ids((prev) => [...prev, id])
    } else {
      setSelectedComments((prev) =>
        prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
      )
    }
  }

  const onImagePopupOpen = () => {
    setIsImagePopupOpen(true)
  }

  const onImagePopupClose = () => {
    setIsImagePopupOpen(false)
  }

  const onImageUpload = (newImage) => {
    let data = new FormData()
    data.append("media", newImage)
    data.append("media_type", "IMAGE")
    data.append("content_type", newImage.type)
    dispatch(uploadSuggestedQuestionImageAction(data))
  }

  const onImageDelete = () => {
    setSelectedImage(null)
    dispatch(learningDevelopmentActions.setSuggestedQuestionImage(null))
  }

  const onQuestionTypeSelect = (e, formik) => {
    const newQuestionType = e.value
    const currentQuestionType = formik.values?.questionType || previousQuestionType
    const currentOptions = formik.values?.optionSet || []

    // Store original options before switching to 2-images
    if (newQuestionType === "2-images" && currentQuestionType !== "2-images") {
      // Backup original options if we have more than 2 and they haven't been backed up yet
      if (currentOptions.length > 2 && !originalOptionsBackup) {
        setOriginalOptionsBackup([...currentOptions])
      }
    }

    // Restore original options when switching back from 2-images to other types
    if (
      currentQuestionType === "2-images" &&
      newQuestionType !== "2-images" &&
      originalOptionsBackup &&
      originalOptionsBackup.length > 2
    ) {
      const restoredOptions = originalOptionsBackup?.map((option) => {
        if (newQuestionType === "matching") {
          return {
            ...option,
            answer: option.answer || "",
            name: option.name || ""
          }
        }
        return {
          ...option,
          name: option.name || "",
          isCorrect: option.isCorrect !== undefined ? option.isCorrect : false
        }
      })
      formik.setFieldValue("optionSet", restoredOptions)
      setOriginalOptionsBackup(null)
    } else {
      // Normal type transformation
      if (newQuestionType === "matching") {
        const updatedOptions =
          currentOptions?.length > 0
            ? currentOptions?.map((option) => ({
                ...option,
                answer: option.answer || "",
                name: option.name || ""
              }))
            : [{ name: "", answer: "" }]
        formik.setFieldValue("optionSet", updatedOptions)
      } else if (currentOptions.length > 0) {
        const updatedOptions = currentOptions.map((option) => ({
          ...option,
          name: option.name || option.answer || "",
          isCorrect: option.isCorrect !== undefined ? option.isCorrect : false
        }))
        formik.setFieldValue("optionSet", updatedOptions)
      }
    }

    // Update previous question type for next change
    setPreviousQuestionType(currentQuestionType)
    formik.setFieldValue("questionType", newQuestionType)
  }

  const initFormikData = () => {
    const correctCount = data.optionSet?.filter((opt) => opt.isCorrect).length || 0
    const selection_mode = correctCount > 1 ? "MULTIPLE" : "SINGLE"

    const isMatchingType = data?.question_type === "matching"
    const optionSet = isMatchingType
      ? data?.match_pairs?.map((option) => ({
          id: option.id,
          name: option.left_text,
          answer: option.right_text
        })) || [{ name: "", answer: "" }]
      : data?.answer?.map((option) => ({
          id: option.id,
          name: option.answer,
          image: option.answer_image
            ? { id: option.answer_image, url: option.answer_image_url }
            : "",
          isCorrect: option.is_correct
        })) || [{ name: "", isCorrect: false }]

    // Reset backup and previous type when initializing with new data
    setOriginalOptionsBackup(null)
    setPreviousQuestionType(data?.question_type || null)

    setQuestion({
      questionId: data.id,
      question: data?.title ?? "",
      category: data?.category_id ?? "",
      category_name: data?.category ?? "",
      relatedFood: data?.related_food ?? "",
      filterCategory: data?.filter_category ?? "",
      difficultyLevel: data.level,
      duration: data.duration,
      optionSet,
      rank: data?.rank,
      questionType: data?.question_type,
      options_count: data?.optionSet?.length,
      selection_mode
    })
    dispatch(
      learningDevelopmentActions.setSuggestedQuestionImage({
        id: data.image_id,
        url: data.image
      })
    )
  }

  const initQuestionOptions = () => {
    setQuestionInitOption(
      data?.answer?.map((option) => ({
        id: option.id,
        name: option.answer,
        image: option.answer_image ? { id: option.answer_image, url: option.answer_image_url } : "",
        isCorrect: option.is_correct
      }))
    )
  }

  const handleSubmit = (values) => {
    const isMatchingType = values?.questionType === "matching"
    const correctCount = isMatchingType
      ? values.optionSet?.length || 0
      : values.optionSet?.filter((opt) => opt.isCorrect).length || 0
    const selection_mode = correctCount > 1 ? "MULTIPLE" : "SINGLE"

    let payload = {
      image: image?.id || null,
      title: values.question,
      category: data?.category_id,
      filter_category: data?.filter_category,
      relatedFood: data?.related_food ?? "",
      duration: data.duration,
      level: data.level,
      ...(values?.questionType === "matching"
        ? {
            match_pairs: values?.optionSet?.map((option, idx) => {
              return {
                left_text: option.name,
                right_text: option.answer,
                order: idx + 1,
                id: option.id
              }
            })
          }
        : {
            answers: values?.optionSet?.map((option, index) => ({
              id: option.id,
              answer: option.name,
              ...(option.answer_image || flavourImages[index]
                ? {
                    answer_image: option?.answer_image
                      ? option?.answer_image
                      : flavourImages[index]
                      ? flavourImages[index]
                      : null
                  }
                : {}),
              is_correct: option.isCorrect
            }))
          }),
      rank: values?.rank,
      question_type: values?.questionType,
      options_count: values?.optionSet?.length,
      selection_mode
    }
    dispatch(
      editChallengeQuestionAction({
        questionId: data.id,
        questionData: payload,
        platform: "All",
        question_used: "All",
        weeklyChallenge: false,
        onClose: () => onClose(),
        archived: activeTab === "archived" ? "True" : "False"
      })
    )
  }
  const handleUpdateCommentStatus = (status) => {
    const payload = {
      is_all_selected: isAllSelected,
      comment_ids: isAllSelected
        ? []
        : selectedComments?.filter((id) => !excepted_ids.includes(id)),
      excepted_ids: isAllSelected ? [] : excepted_ids,
      status: status
    }
    dispatch(updateCommentStatusAction({ payload, id: data?.id }))
  }

  useEffect(() => {
    setSelectedComments([])
    setExcepted_ids([])
  }, [tab])

  useEffect(() => {
    initFormikData()
    initQuestionOptions()
  }, [data, weeklyCategoryList])

  return (
    <StyledDialog open={true} onClose={onClose} maxWidth="">
      <ImageUploadPopup
        onClose={onImagePopupClose}
        open={isImagePopupOpen}
        aspectHeight={1}
        aspectWidth={1}
        {...{
          selectedImage,
          setSelectedImage,
          onImageUpload,
          onImageDelete
        }}
      />
      <DialogContent
        id="editQuestionDialog"
        sx={{
          overflow: "visible",
          padding: 0,
          gridTemplateColumns: showComments ? "1fr 460px" : "1fr"
        }}
        className={styles.dialogBox}>
        <Box className={styles.questionContainer}>
          <Formik
            initialValues={question}
            validationSchema={validationSchemaEditWeeklyQuestion}
            context={{ questionType: question?.questionType }}
            enableReinitialize={true}
            onSubmit={handleSubmit}>
            {(formik) => (
              <React.Fragment>
                <Box className="d-flex justify-content-center position-relative">
                  <Typography variant="h2">{`Edit Question`}</Typography>
                </Box>

                <IconButton
                  onClick={onClose}
                  className={styles.closeBtn}
                  sx={{
                    top: 15,
                    right: 15,
                    height: 32,
                    width: 32,
                    position: "absolute",
                    backgroundColor: theme.palette.primary.hover,
                    "&:hover": { backgroundColor: theme.palette.primary.hover }
                  }}>
                  <CloseIcon />
                </IconButton>

                <Box height={40} />
                <Box sx={{ padding: "0 11px" }}>
                  <Box className="d-flex justify-content-end mb-4">
                    <Typography
                      variant="caption5"
                      gutterBottom
                      onClick={() => setShowComments((prev) => !prev)}
                      sx={{
                        textDecoration: "underline",
                        color: theme.palette.secondary.main,
                        textUnderlineOffset: "7px",
                        cursor: "pointer"
                      }}>
                      {showComments ? "Hide Comments" : "View Comments"}
                    </Typography>
                  </Box>
                  <Grid item container xs={12} justifyContent={"space-between"}>
                    <Grid xs={4}>
                      <Typography variant="h2" sx={{ fontSize: 20 }}>
                        Question Rank
                      </Typography>
                      {showRank && (
                        <Grid item mt={2}>
                          <Grid container xs={12} alignContent="center">
                            {RankOpt?.map((rank, index) => (
                              <Grid
                                item
                                key={index}
                                className={
                                  formik.values?.rank !== rank ? styles.rank : styles.rank_selected
                                }
                                style={{
                                  marginRight: index !== RankOpt.length - 1 ? "24px" : "0px"
                                }}
                                onClick={() => formik.setFieldValue("rank", rank)}>
                                {rank}
                              </Grid>
                            ))}
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                    <Grid
                      item
                      xs={3}
                      display={"flex"}
                      flexDirection={"column"}
                      justifyContent={"flex-end"}>
                      <Typography variant="h2" sx={{ fontSize: 20 }}>
                        Question Type
                      </Typography>
                      <Grid item mt={2}>
                        <AppSelect
                          id={`formik.values.questionType`}
                          name={`formik.values.questionType`}
                          menuItems={questionTypeChoices}
                          isRequired={true}
                          value={formik.values?.questionType}
                          onMenuClick={(e) => onQuestionTypeSelect(e, formik)}
                          error={formik.touched.questionType && formik.errors.questionType}
                          touched={formik.touched.questionType}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Box height={19} />
                  <Typography variant="h2" sx={{ fontSize: 20 }}>
                    Question
                  </Typography>
                  <Box height={19} />
                  <Box className="d-flex">
                    <AppTextField
                      id="question"
                      name="question"
                      placeholder="Enter Question"
                      value={formik.values.question}
                      onChange={formik.handleChange}
                      className="flex-grow-1 flex-shrink-1"
                      fullWidth
                      error={formik.touched.question && Boolean(formik.errors.question)}
                      helperText={formik.touched.question && formik.errors.question}
                    />
                    <img
                      role="button"
                      onClick={onImagePopupOpen}
                      src={ImageIcon}
                      className="flex-shrink-0 ms-4"
                      alt="Upload Image"
                    />
                  </Box>
                  {image?.url ? (
                    <Box className="mt-4 position-relative" sx={{ width: "fit-content" }}>
                      <img
                        style={{ height: 182, borderRadius: 6, width: "100%", maxWidth: "250px" }}
                        src={image.url}
                        alt=""
                      />
                      <img
                        role="button"
                        src={DeleteImageIcon}
                        alt=""
                        className="position-absolute"
                        style={{ top: 12, right: 12 }}
                        onClick={onImageDelete}
                      />
                    </Box>
                  ) : null}
                  {!!formik?.values?.questionType && formik?.values?.questionType === "matching" ? (
                    <DraggableMatchingOptions
                      formik={formik}
                      getOptionValues={(values = formik.values) => values?.optionSet}
                      getOptionPath={() => `optionSet`}
                    />
                  ) : !!formik?.values?.questionType &&
                    formik?.values?.questionType !== "text" &&
                    formik?.values?.questionType !== "soundbite" ? (
                    <DraggableImageOptions
                      formik={formik}
                      getOptionValues={(values = formik.values) => values?.optionSet}
                      getQuestionType={() => formik?.values?.questionType}
                      getOptionPath={() => `optionSet`}
                      questionInitOption={questionInitOption}
                    />
                  ) : (
                    <DraggableOptions
                      formik={formik}
                      getOptionValues={(values = formik.values) =>
                        values?.optionSet?.length !== 2 ? values?.optionSet : questionInitOption
                      }
                      getOptionPath={() => `optionSet`}
                    />
                  )}
                  <Box className="d-flex justify-content-center">
                    <AppButton
                      className="mt-3 px-5"
                      variant="outlined"
                      onClick={formik.handleSubmit}>
                      Save
                    </AppButton>
                  </Box>
                </Box>
              </React.Fragment>
            )}
          </Formik>
        </Box>
        {showComments && (
          <Box className={styles.commentContainer}>
            <Box className={styles.topBar}>
              <Typography variant="h2" className={styles.title}>
                {` Comments (${commentsData?.counts?.total})`}
              </Typography>
            </Box>

            <Box className="d-flex justify-content-center">
              <Tabs
                value={tab}
                onChange={(e, newVal) => setTab(newVal)}
                classes={{
                  root: styles.tabs,
                  indicator: styles.tabIndicator
                }}>
                <Tab
                  value={"open_comments"}
                  label={
                    <Typography variant="body4Regular" color={theme.palette.text.secondary}>
                      {`Open (${commentsData?.counts?.open})`}
                    </Typography>
                  }
                  className={styles.tab}
                />
                <Tab
                  value={"resolved_comments"}
                  label={
                    <Typography variant="body4Regular">{`Resolved (${commentsData?.counts?.resolved})`}</Typography>
                  }
                  className={styles.tab}
                />
                <Tab
                  value={"irrelevant_comments"}
                  label={
                    <Typography variant="body4Regular">{`Irrelevant (${commentsData?.counts?.irrelevant})`}</Typography>
                  }
                  className={styles.tab}
                />
              </Tabs>
            </Box>
            {commentsData?.[tab]?.length ? (
              <>
                <Box className={styles.actionBar}>
                  {isAllSelected ? (
                    <CheckCircleIcon
                      role="button"
                      htmlColor={theme.palette.secondary.main}
                      onClick={handleToggleAll}
                    />
                  ) : (
                    <RadioButtonUncheckedIcon role="button" onClick={handleToggleAll} />
                  )}
                  <Typography className={styles.selectAll}>Select All</Typography>

                  {statusOptions[tab]?.map((btn, index) => {
                    const isDisabled = selectedComments?.length === 0
                    return isDisabled ? (
                      <Tooltip key={index} title={btn.tooltip} placement="top">
                        <span>
                          {" "}
                          <AppButton
                            key={index}
                            disabled={isDisabled}
                            startIcon={btn.icon}
                            onClick={() => handleUpdateCommentStatus(btn.status)}>
                            {btn.label}
                          </AppButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <AppButton
                        key={index}
                        disabled={isDisabled}
                        startIcon={btn.icon}
                        onClick={() => handleUpdateCommentStatus(btn.status)}>
                        {btn.label}
                      </AppButton>
                    )
                  })}
                </Box>
                <Box className={styles.commentList}>
                  {commentsData?.[tab]?.map((comment, index) => (
                    <Comment
                      key={index}
                      comment={comment}
                      tab={tab}
                      selected={selectedComments?.includes(comment?.id)}
                      onToggle={() => handleToggleOne(comment?.id)}
                      questionId={data?.id}
                    />
                  ))}
                </Box>
              </>
            ) : (
              <Box className="d-flex justify-content-center align-items-center mt-4">
                <Typography variant="body4Regular" color={theme.palette.text.secondary}>
                  {noCommentsTextMap[tab] || "No comments available"}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </StyledDialog>
  )
}

export default EditQuestion
